import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Alert, Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import adminApi from "../api/adminApi";
import { useAuth } from "../context/AuthContext";

const EMPTY_FORM = { name: "", email: "", password: "", role: "editor" };

const UserDialog = ({ open, onClose, user, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(user ? { name: user.name, email: user.email, role: user.role, password: "" } : EMPTY_FORM);
    setError("");
  }, [user, open]);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (user?._id) {
        await adminApi.put(`/users/${user._id}`, payload);
      } else {
        await adminApi.post("/users", payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{user?._id ? "Edit User" : "New User"}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}><TextField label="Full Name" fullWidth value={form.name} onChange={set("name")} /></Grid>
          <Grid item xs={12}><TextField label="Email" type="email" fullWidth value={form.email} onChange={set("email")} /></Grid>
          <Grid item xs={12}>
            <TextField label={user?._id ? "New Password (leave blank to keep)" : "Password"} type="password"
              fullWidth value={form.password} onChange={set("password")} />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={form.role} label="Role" onChange={set("role")}>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ backgroundColor: "#1C2541" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UserManager = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await adminApi.get("/users");
    setUsers(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this user?")) return;
    await adminApi.delete(`/users/${id}`);
    load();
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold" color="#1C2541">User Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelected(null); setDialogOpen(true); }}
          sx={{ backgroundColor: "#1C2541" }}>
          Add User
        </Button>
      </Box>

      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u._id} hover>
                <TableCell>{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Chip label={u.role} color={u.role === "admin" ? "primary" : "default"} size="small" /></TableCell>
                <TableCell><Chip label={u.active ? "Active" : "Inactive"} color={u.active ? "success" : "default"} size="small" /></TableCell>
                <TableCell><Typography variant="caption">{new Date(u.createdAt).toLocaleDateString()}</Typography></TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => { setSelected(u); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                  {u._id !== currentUser?.id && (
                    <IconButton size="small" color="error" onClick={() => handleDeactivate(u._id)}><BlockIcon fontSize="small" /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <UserDialog open={dialogOpen} onClose={() => setDialogOpen(false)} user={selected} onSaved={load} />
    </Box>
  );
};

export default UserManager;
